import { type ViewMetadata } from '@/types'
import Dictionary from './Dictionary'
import type { JSX } from 'react'
import { Error } from '../Error'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

/**
 * PrintPreview component.
 *
 * This component renders a preview of a document using an iframe. It includes a header
 * with actions and a scrollable area to view the document. The document is fetched from
 * a predefined URL and displayed within the iframe.
 *
 * @returns The rendered PrintPreview component.
 *
 * @remarks
 * The component uses a responsive layout with a maximum width of 1200px. The iframe
 * is set to a fixed height of 980px and takes the full width of its container.
 */


// Metadata definition
const meta: ViewMetadata = {
  name: 'PrintDictionary',
  path: `${import.meta.env.BASE_URL || ''}/print-dictionary`,
  widths: {
    sm: 12,
    md: 12,
    lg: 4,
    xl: 4,
    '2xl': 4,
    hd: 3,
    fhd: 3,
    qhd: 3,
    uhd: 2
  }
}

// Main Editor Component - Handles document initialization
const PrintDictionary = (): JSX.Element | null => {
  const featureFlags = useFeatureFlags(['hasPrint'])
  const { t } = useTranslation('errors')

  if (!featureFlags.hasPrint) {
    return (
      <Error
        title={t('messages.errorTitle')}
        message={t('messages.unknownErrorAdminInfo')}
      />
    )
  }

  return (
    <Dictionary className='w-full' />
  )
}

PrintDictionary.meta = meta

export { PrintDictionary }
